import { useCallback, useRef, useState } from 'react';
import type { DrawingDocument } from '../generated/drawing-contract';
import { setPartialCharges } from '@peruselab/structure-editor/domain/graphDrawing';
import { partialChargeResult } from '@peruselab/structure-editor/domain/documentSelectors';
import { moleculeSourceKey, toChemMoleculeV1 } from '@peruselab/structure-editor/domain/moleculeProjection';
import { computePartialCharges } from '@peruselab/api-client/partialCharges';

type PartialChargeState =
  { readonly state: 'idle' } | { readonly state: 'loading' } | { readonly state: 'error'; readonly message: string };

// Explicit user-triggered action, not reactive/debounced like aromaticity/CIP perception --
// see computePartialCharges for why (no client-side RDKit-JS charge API).
export function usePartialCharges(
  document: DrawingDocument,
  applyWithoutHistory: (change: (current: DrawingDocument) => DrawingDocument) => void
) {
  const [state, setState] = useState<PartialChargeState>({ state: 'idle' });
  const requestRef = useRef(0);

  const runCompute = useCallback(async () => {
    if (document.structure.graph.nodes.length === 0) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const requestedSourceKey = moleculeSourceKey(document);
    setState({ state: 'loading' });
    try {
      const result = await computePartialCharges(toChemMoleculeV1(document));
      if (requestRef.current !== requestId) return;
      setState({ state: 'idle' });
      // Guards against the structure having changed while the request was in flight -- a stale
      // result must not get silently stamped onto a molecule it was never computed for.
      applyWithoutHistory((current) =>
        moleculeSourceKey(current) === requestedSourceKey
          ? setPartialCharges(current, result.charges, result.provenance)
          : current
      );
    } catch (error) {
      if (requestRef.current !== requestId) return;
      setState({
        state: 'error',
        message: error instanceof Error ? error.message : 'Partial charge computation failed.'
      });
    }
  }, [applyWithoutHistory, document]);

  return { state, result: partialChargeResult(document), runCompute };
}
